import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, ChevronDown, ChevronRight } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  firstOfMonth, today, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, Pager, Dash, TabBar, errMessage, DEFAULT_PAGE_SIZE,
} from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { PagedResult } from '@/types/api.types';
import type {
  SourceTraceFilter, SourceTraceRow,
  PostingSummaryReportDto,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'SOURCE_TRACE';

const POSTING_STATUS_TONES: Record<string, PillTone> = {
  POSTED:     'green',
  NOT_POSTED: 'grey',
  FAILED:     'red',
  CANCELLED:  'amber',
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

const TRACE_COLUMNS: ExcelColumn[] = [
  { key: 'Module',    header: 'Source Module', width: 16 },
  { key: 'SourceDoc', header: 'Source Doc',    width: 20 },
  { key: 'Journal',   header: 'GL Journal',    width: 18 },
  { key: 'DocDate',   header: 'Document Date', width: 12 },
  { key: 'PostDate',  header: 'Posting Date',  width: 12 },
  { key: 'Debit',     header: 'Total Debit',   width: 16, numFmt: '#,##0.00' },
  { key: 'Credit',    header: 'Total Credit',  width: 16, numFmt: '#,##0.00' },
  { key: 'Workflow',  header: 'Workflow',      width: 16 },
  { key: 'Posting',   header: 'Posting',       width: 12 },
  { key: 'LineCount', header: 'Lines',         width: 8 },
];

const SUMMARY_COLUMNS: ExcelColumn[] = [
  { key: 'Module',     header: 'Source Module', width: 18 },
  { key: 'Journals',   header: 'Journals',      width: 10 },
  { key: 'Debit',      header: 'Total Debit',   width: 16, numFmt: '#,##0.00' },
  { key: 'Credit',     header: 'Total Credit',  width: 16, numFmt: '#,##0.00' },
  { key: 'Batches',    header: 'Batches',       width: 10 },
  { key: 'Failed',     header: 'Failed',        width: 10 },
  { key: 'DeadLetter', header: 'Dead-Letter',   width: 12 },
  { key: 'LastPosted', header: 'Last Posted',   width: 18 },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const SourceTracePage = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const [tab, setTab] = useState('trace');

  // Shared date window (each tab reads it when it runs)
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());

  // Tab 1 — source → GL trace
  const [sourceModule, setSourceModule] = useState('');
  const [sourceDocumentNo, setSourceDocumentNo] = useState('');
  const [trace, setTrace] = useState<PagedResult<SourceTraceRow> | null>(null);
  const [traceApplied, setTraceApplied] = useState<SourceTraceFilter | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Tab 2 — posting summary
  const [summary, setSummary] = useState<PostingSummaryReportDto | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const fetchTrace = useCallback(async (filter: SourceTraceFilter) => {
    setTraceLoading(true);
    setTraceError(null);
    try {
      const res = await periodReportApi.getSourceTrace(filter);
      setTrace(res.data?.data ?? null);
      setTraceApplied(filter);
      setExpanded(new Set());
    } catch (e: unknown) {
      setTraceError(errMessage(e));
    } finally {
      setTraceLoading(false);
    }
  }, []);

  const runTrace = useCallback(() => {
    void fetchTrace({
      fromDate,
      toDate,
      sourceModule: sourceModule.trim() || undefined,
      sourceDocumentNo: sourceDocumentNo.trim() || undefined,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });
  }, [fromDate, toDate, sourceModule, sourceDocumentNo, fetchTrace]);

  const runSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await periodReportApi.getPostingSummary({ fromDate, toDate });
      setSummary(res.data?.data ?? null);
    } catch (e: unknown) {
      setSummaryError(errMessage(e));
    } finally {
      setSummaryLoading(false);
    }
  }, [fromDate, toDate]);

  const toggleExpand = (journalId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(journalId)) next.delete(journalId);
      else next.add(journalId);
      return next;
    });
  };

  if (!hasPermission(Permissions.RPT_SOURCE_TRACE)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const traceRows = trace?.items ?? [];
  const summaryRows = summary?.rows ?? [];
  const isTraceTab = tab === 'trace';

  const handleExcel = () => {
    if (isTraceTab) {
      if (!trace) return;
      exportToExcel({
        sheetName: 'Source Trace',
        columns: TRACE_COLUMNS,
        rows: traceRows.map(r => ({
          Module: r.sourceModule, SourceDoc: r.sourceDocumentNo ?? '', Journal: r.journalNumber,
          DocDate: r.documentDate, PostDate: r.postingDate, Debit: r.totalDebit, Credit: r.totalCredit,
          Workflow: r.workflowStatus, Posting: r.postingStatus, LineCount: r.totalLineCount,
        })),
        title: 'Source Document → GL Trace',
        subtitle: `${fromDate} – ${toDate}`,
      }, `SourceTrace_${fromDate}_${toDate}`);
    } else {
      if (!summary) return;
      exportToExcel({
        sheetName: 'Posting Summary',
        columns: SUMMARY_COLUMNS,
        rows: summaryRows.map(r => ({
          Module: r.sourceModule, Journals: r.journalCount, Debit: r.totalDebit, Credit: r.totalCredit,
          Batches: r.batchCount, Failed: r.failedCount, DeadLetter: r.deadLetterCount,
          LastPosted: r.lastPostedDate ?? '',
        })),
        title: 'Posting Summary by Source Module',
        subtitle: `${summary.companyName} · ${summary.fromDate} – ${summary.toDate}`,
      }, `PostingSummary_${fromDate}_${toDate}`);
    }
  };

  const handleCsv = () => {
    if (isTraceTab) {
      if (!trace) return;
      exportToCsvRows(TRACE_COLUMNS, traceRows.map(r => ({
        Module: r.sourceModule, SourceDoc: r.sourceDocumentNo ?? '', Journal: r.journalNumber,
        DocDate: r.documentDate, PostDate: r.postingDate, Debit: r.totalDebit, Credit: r.totalCredit,
        Workflow: r.workflowStatus, Posting: r.postingStatus, LineCount: r.totalLineCount,
      })), `SourceTrace_${fromDate}_${toDate}`);
    } else {
      if (!summary) return;
      exportToCsvRows(SUMMARY_COLUMNS, summaryRows.map(r => ({
        Module: r.sourceModule, Journals: r.journalCount, Debit: r.totalDebit, Credit: r.totalCredit,
        Batches: r.batchCount, Failed: r.failedCount, DeadLetter: r.deadLetterCount,
        LastPosted: r.lastPostedDate ?? '',
      })), `PostingSummary_${fromDate}_${toDate}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitBranch size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Source Document Trace</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={isTraceTab ? !!trace : !!summary}
          reportKey={REPORT_KEY}
          rowCount={isTraceTab ? traceRows.length : summaryRows.length}
          filterSummary={`from=${fromDate} to=${toDate}${sourceModule ? ` module=${sourceModule}` : ''}`}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'trace', label: 'Source → GL' },
          { key: 'summary', label: 'Posting Summary' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <label className={LABEL_CLS}>From *</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
            </div>
            <div className="w-40">
              <label className={LABEL_CLS}>To *</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
            </div>
            {isTraceTab && (
              <>
                <div className="w-44">
                  <label className={LABEL_CLS}>Source Module</label>
                  <input
                    type="text"
                    value={sourceModule}
                    onChange={e => setSourceModule(e.target.value)}
                    placeholder="e.g. AR, AP, BANKING"
                    className={INPUT_CLS}
                  />
                </div>
                <div className="w-48">
                  <label className={LABEL_CLS}>Source Document No</label>
                  <input
                    type="text"
                    value={sourceDocumentNo}
                    onChange={e => setSourceDocumentNo(e.target.value)}
                    placeholder="e.g. INV-000123"
                    className={INPUT_CLS}
                  />
                </div>
              </>
            )}
          </div>
          <RunReportButton
            onClick={() => { if (isTraceTab) runTrace(); else void runSummary(); }}
            isLoading={isTraceTab ? traceLoading : summaryLoading}
            disabled={!fromDate || !toDate}
          />
        </div>
      </div>

      {isTraceTab ? (
        <>
          {traceError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {traceError}
            </div>
          )}

          {traceLoading && !trace && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!trace && !traceLoading && (
            <EmptyState message='Set a posting-date window and click "Run Report" to trace source documents into their GL journals.' />
          )}

          {trace && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1200px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={cn(TH_CLS, 'w-8')} />
                      <th className={TH_CLS}>Source Module</th>
                      <th className={TH_CLS}>Source Doc</th>
                      <th className={TH_CLS}>GL Journal</th>
                      <th className={cn(TH_CLS, 'w-28')}>Doc Date</th>
                      <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                      <th className={TH_R_CLS}>Total Debit</th>
                      <th className={TH_R_CLS}>Total Credit</th>
                      <th className={TH_CLS}>Workflow</th>
                      <th className={TH_CLS}>Posting</th>
                      <th className={cn(TH_R_CLS, 'w-16')}>Lines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traceRows.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No journals match the selected criteria.
                        </td>
                      </tr>
                    ) : traceRows.map(r => {
                      const isOpen = expanded.has(r.journalId);
                      const capped = r.totalLineCount > r.lines.length;
                      return (
                        <SourceTraceRowGroup
                          key={r.journalId}
                          row={r}
                          isOpen={isOpen}
                          capped={capped}
                          onToggle={() => toggleExpand(r.journalId)}
                          onOpenJournal={() => navigate(`/gl/journals/${r.journalId}`)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager
                pageNumber={trace.pageNumber}
                totalPages={trace.totalPages}
                totalCount={trace.totalCount}
                shownCount={traceRows.length}
                loading={traceLoading}
                onPage={p => {
                  if (traceApplied) void fetchTrace({ ...traceApplied, pageNumber: p });
                }}
                noun="journals"
              />
            </div>
          )}
        </>
      ) : (
        <>
          {summaryError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {summaryError}
            </div>
          )}

          {summaryLoading && !summary && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!summary && !summaryLoading && (
            <EmptyState message='Set a posting-date window and click "Run Report" for a per-module posting summary including MANUAL and SCHEDULED journals.' />
          )}

          {summary && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{summary.companyName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Posting summary · {fmtDate(summary.fromDate)} – {fmtDate(summary.toDate)} · Generated {fmtDate(summary.generatedAt)}
                </div>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1000px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={TH_CLS}>Source Module</th>
                      <th className={TH_R_CLS}>Journals</th>
                      <th className={TH_R_CLS}>Total Debit</th>
                      <th className={TH_R_CLS}>Total Credit</th>
                      <th className={TH_R_CLS}>Batches</th>
                      <th className={TH_R_CLS}>Failed</th>
                      <th className={TH_R_CLS}>Dead-Letter</th>
                      <th className={TH_CLS}>Last Posted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No journals were posted in the selected window.
                        </td>
                      </tr>
                    ) : summaryRows.map(r => (
                      <tr key={r.sourceModule} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {r.sourceModule === 'MANUAL' || r.sourceModule === 'SCHEDULED'
                            ? <Pill label={r.sourceModule} tone={r.sourceModule === 'MANUAL' ? 'blue' : 'purple'} />
                            : r.sourceModule}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{r.journalCount.toLocaleString('en-US')}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalCredit)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{r.batchCount.toLocaleString('en-US')}</td>
                        <td className={cn(
                          AMT_CLS,
                          r.failedCount > 0 ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                        )}>
                          {r.failedCount.toLocaleString('en-US')}
                        </td>
                        <td className={cn(
                          AMT_CLS,
                          r.deadLetterCount > 0 ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600',
                        )}>
                          {r.deadLetterCount.toLocaleString('en-US')}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {r.lastPostedDate ? fmtDateTime(r.lastPostedDate) : <Dash />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Expandable trace row ──────────────────────────────────────────────────────

const SourceTraceRowGroup = ({ row: r, isOpen, capped, onToggle, onOpenJournal }: {
  row: SourceTraceRow;
  isOpen: boolean;
  capped: boolean;
  onToggle: () => void;
  onOpenJournal: () => void;
}) => (
  <>
    <tr
      onClick={onToggle}
      className="border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
      title="Show GL lines"
    >
      <td className="px-3 py-2 text-gray-400">
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </td>
      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.sourceModule}</td>
      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.sourceDocumentNo ?? <Dash />}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <button
          onClick={e => { e.stopPropagation(); onOpenJournal(); }}
          className="text-sm font-mono text-brand-600 dark:text-brand-400 hover:underline"
          title="Open GL journal"
        >
          {r.journalNumber}
        </button>
      </td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.documentDate)}</td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalCredit)}</td>
      <td className="px-3 py-2"><Pill label={r.workflowStatus} tone={r.workflowStatus === 'POSTED' ? 'green' : 'grey'} /></td>
      <td className="px-3 py-2"><Pill label={r.postingStatus} tone={POSTING_STATUS_TONES[r.postingStatus] ?? 'grey'} /></td>
      <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>{r.totalLineCount}</td>
    </tr>
    {isOpen && (
      <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
        <td colSpan={11} className="px-6 py-3">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            GL Lines
          </div>
          <table className="w-full max-w-3xl text-xs border-collapse">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4 text-left font-medium w-10">#</th>
                <th className="py-1 pr-4 text-left font-medium">Account</th>
                <th className="py-1 pr-4 text-left font-medium">Name</th>
                <th className="py-1 pr-4 text-right font-medium">Debit</th>
                <th className="py-1 text-right font-medium">Credit</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map(l => (
                <tr key={l.lineNumber} className="border-t border-gray-200/60 dark:border-gray-700/60 text-gray-700 dark:text-gray-300">
                  <td className="py-1.5 pr-4 tabular-nums">{l.lineNumber}</td>
                  <td className="py-1.5 pr-4 font-mono">{l.accountCode}</td>
                  <td className="py-1.5 pr-4">{l.accountName}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtAmtZero(l.debit)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtAmtZero(l.credit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {capped && (
            <div className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              +{r.totalLineCount - r.lines.length} more lines — open the journal for the full detail.
            </div>
          )}
        </td>
      </tr>
    )}
  </>
);
